// Dashboard: 8 metric cards + 3 panels (My tasks, All tasks, Recent Proposals)
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { formatDisplayDate as formatProposalDate, formatDisplayWeekdayDate as formatDate, formatDisplayTime } from './util/formatDisplayDate';

const STORAGE_KEY = 'sft_dashboard_cards';

const METRIC_IDS = [
  'products',
  'clients',
  'proposals',
  'subscriptions',
  'warehouses',
  'stock_value',
  'receipts_7d',
  'issues_7d'
];

// Порядок карточек на дашборде; в модалке выбираются только они — отображаем ровно столько, сколько выбрано
const GRID_ORDER = ['products', 'clients', 'proposals', 'subscriptions', 'warehouses', 'stock_value', 'my_tasks', 'all_tasks', 'recent_proposals', 'recent_clients', 'sales_reminders', 'patrol_data_changed'];

const CARD_LABELS = {
  products: 'Products',
  clients: 'Clients',
  proposals: 'Proposals',
  subscriptions: 'Subscriptions',
  warehouses: 'Warehouses',
  stock_value: 'Stock Value',
  receipts_7d: 'Receipts (7 days)',
  issues_7d: 'Issues (7 days)',
  recent_proposals: 'Recent Proposals',
  recent_clients: 'Recent Clients',
  recent_receipts: 'Recent Receipts',
  recent_issues: 'Recent Issues',
  my_tasks: 'My tasks',
  all_tasks: 'All tasks',
  sales_reminders: 'Sales reminders (today)',
  patrol_data_changed: 'Patrol – data updated, resend required'
};

const VIEW_ALL_LINKS = {
  products: '/items',
  clients: '/clients/',
  proposals: '/proposals',
  subscriptions: '/subscriptions/',
  warehouses: '/warehouse/?tab=Warehouses',
  stock_value: '/warehouse/?tab=Stock',
  receipts_7d: '/warehouse/?tab=Receipts',
  issues_7d: '/warehouse/?tab=Issues',
  my_tasks: '/scheduler',
  all_tasks: '/scheduler',
  recent_proposals: '/proposals',
  recent_clients: '/clients/',
  sales_reminders: '/sales/reminders',
  patrol_data_changed: '/patrol-admin?dataChanged=yes'
};

function loadSelectedCards() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr) && arr.length) return arr;
    }
  } catch (_) {}
  return [...GRID_ORDER];
}

function saveSelectedCards(ids) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch (_) {}
}

/** overdue | expiring | future */
function getTaskDueStatus(dueAt) {
  if (!dueAt) return 'future';
  const due = new Date(dueAt);
  if (isNaN(due.getTime())) return 'future';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(due);
  dueDate.setHours(0, 0, 0, 0);
  if (dueDate.getTime() < today.getTime()) return 'overdue';
  const in2Days = new Date(today);
  in2Days.setDate(in2Days.getDate() + 2);
  if (dueDate.getTime() <= in2Days.getTime()) return 'expiring';
  return 'future';
}

function formatProposalId(p) {
  const id = p.proposalId ?? p._id ?? '';
  if (id === '' || id === undefined || String(id) === 'undefined') return '—';
  const s = String(id);
  return s.startsWith('SFT-') ? s : `SFT-${s}`;
}

function formatProposalClient(p) {
  const c = p?.clientInfo;
  if (!c) return '—';
  const name = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
  const company = c.companyName || c.company || '';
  if (company && name) return `${name} • ${company}`;
  if (company) return company;
  if (name) return name;
  return c.email || '—';
}

export default function DashboardPage() {
  const [selectedIds, setSelectedIds] = useState(loadSelectedCards);
  const [showModal, setShowModal] = useState(false);
  const [draftIds, setDraftIds] = useState([]);
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [recentClients, setRecentClients] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [patrolDataChanged, setPatrolDataChanged] = useState([]);
  const [salesReminders, setSalesReminders] = useState([]);

  useEffect(() => {
    setSelectedIds(loadSelectedCards());
  }, []);

  useEffect(() => {
    fetch('/auth/me', { credentials: 'include', cache: 'no-store' })
      .then((r) => r.ok ? r.json() : null)
      .then((j) => j?.ok && j?.user && setUser(j.user))
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);

  // Для companyType 'other' убираем patrol из сохранённых карточек
  useEffect(() => {
    if (user?.companyType === 'other' && selectedIds.includes('patrol_data_changed')) {
      const next = selectedIds.filter((id) => id !== 'patrol_data_changed');
      setSelectedIds(next);
      saveSelectedCards(next);
    }
  }, [user?.companyType, selectedIds]);

  useEffect(() => {
    if (!user) return;
    axios.get('/proposals', { withCredentials: true })
      .then((r) => setProposals(Array.isArray(r.data) ? r.data.slice(0, 5) : []))
      .catch(() => setProposals([]));
    axios.get('/clients', { withCredentials: true })
      .then((r) => setRecentClients(Array.isArray(r.data) ? r.data.slice(0, 5) : []))
      .catch(() => setRecentClients([]));
    axios.get('/dashboard/my-tasks', { withCredentials: true })
      .then((r) => setMyTasks(Array.isArray(r.data) ? r.data : []))
      .catch(() => setMyTasks([]));
    axios.get('/dashboard/all-tasks', { withCredentials: true })
      .then((r) => setAllTasks(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAllTasks([]));
    if (user?.companyType !== 'other') {
      axios.get('/admin/patrol-applications', { withCredentials: true })
        .then((r) => {
          const list = Array.isArray(r.data) ? r.data : [];
          setPatrolDataChanged(list.filter((a) => a.dataChangedAfterSend === true));
        })
        .catch(() => setPatrolDataChanged([]));
    } else {
      setPatrolDataChanged([]);
    }
    if (user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'sales') {
      axios.get('/dashboard/sales-reminders', { withCredentials: true })
        .then((r) => setSalesReminders(Array.isArray(r.data) ? r.data : []))
        .catch(() => setSalesReminders([]));
    }
  }, [user]);

  function openModal() {
    const ids = Array.isArray(selectedIds) ? selectedIds : loadSelectedCards();
    setDraftIds([...ids]);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
  }

  function toggleDraft(id) {
    setDraftIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function saveCards() {
    const ids = draftIds.length ? draftIds : [...GRID_ORDER];
    setSelectedIds(ids);
    saveSelectedCards(ids);
    setShowModal(false);
  }

  const today = formatDate(new Date());

  // Показываем ровно те карточки, что выбраны; если выбраны все — показываем все. Для companyType 'other' (или пока user не загружен) не показываем patrol_data_changed
  const isOther = !user || user?.companyType === 'other';
  const baseOrder = isOther ? GRID_ORDER.filter((id) => id !== 'patrol_data_changed') : GRID_ORDER;
  const visibleIds = baseOrder.filter((id) => selectedIds.includes(id));
  const idsToShow = visibleIds.length > 0 ? visibleIds : [...baseOrder];

  function renderCard(id) {
    if (METRIC_IDS.includes(id)) {
      return <MetricCard key={id} id={id} user={user} />;
    }
    if (id === 'my_tasks') {
      return (
        <div key="my_tasks" className="sft-dashboard-panel">
          <div className="sft-dashboard-panel-head">
            <h3 className="sft-dashboard-panel-title">My tasks</h3>
            <a href={VIEW_ALL_LINKS.my_tasks} className="sft-dashboard-view-all-btn">View All</a>
          </div>
          <div className="sft-dashboard-panel-body">
            {myTasks.length === 0 ? (
              <p className="sft-hint">No current tasks</p>
            ) : (
              <ul className="sft-dashboard-tasks-list">
                {myTasks.map((t) => {
                  const dueStatus = getTaskDueStatus(t.dueAt);
                  return (
                    <li key={t._id} className={`sft-dashboard-task-item sft-dashboard-task-${dueStatus}`}>
                      <a href={`/scheduler?task=${t._id}`} className="sft-dashboard-task-link">
                        {t.title || '—'}
                      </a>
                      <span className="sft-dashboard-task-date">{formatProposalDate(t.dueAt)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      );
    }
    if (id === 'all_tasks') {
      return (
        <div key="all_tasks" className="sft-dashboard-panel">
          <div className="sft-dashboard-panel-head">
            <h3 className="sft-dashboard-panel-title">All tasks</h3>
            <a href={VIEW_ALL_LINKS.all_tasks} className="sft-dashboard-view-all-btn">View All</a>
          </div>
          <div className="sft-dashboard-panel-body">
            {allTasks.length === 0 ? (
              <p className="sft-hint">No current tasks</p>
            ) : (
              <ul className="sft-dashboard-tasks-list">
                {allTasks.map((t) => {
                  const dueStatus = getTaskDueStatus(t.dueAt);
                  return (
                    <li key={t._id} className={`sft-dashboard-task-item sft-dashboard-task-${dueStatus}`}>
                      <a href={`/scheduler?task=${t._id}`} className="sft-dashboard-task-link">
                        {t.title || '—'}
                      </a>
                      <span className="sft-dashboard-task-meta">
                        {Array.isArray(t.assigneeIds) && t.assigneeIds.length
                          ? t.assigneeIds.map((a) => (typeof a === 'object' && a?.username ? a.username : String(a))).join(', ')
                          : '—'}
                      </span>
                      <span className="sft-dashboard-task-date">{formatProposalDate(t.dueAt)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      );
    }
    if (id === 'recent_proposals') {
      return (
        <div key="recent_proposals" className="sft-dashboard-panel">
          <div className="sft-dashboard-panel-head">
            <h3 className="sft-dashboard-panel-title">Recent Proposals</h3>
            <a href={VIEW_ALL_LINKS.recent_proposals} className="sft-dashboard-view-all-btn">View All</a>
          </div>
          <div className="sft-dashboard-panel-body">
            {proposals.length === 0 ? (
              <p className="sft-hint">No proposals</p>
            ) : (
              <ul className="sft-dashboard-proposals-list">
                {proposals.map((p) => {
                  const status = (p.status || 'new').toUpperCase();
                  const statusClass = status === 'ACCEPTED' ? 'accepted' : 'new';
                  const proposalIdForLink = p.proposalId ?? p._id ?? '';
                  return (
                    <li key={p.proposalId || p._id || p._id_ref} className="sft-dashboard-proposal-item">
                      <a href={`/proposal/?id=${proposalIdForLink}`} className="sft-dashboard-proposal-link">
                        <span className="sft-dashboard-proposal-id">{formatProposalId(p)}</span>
                        <span className={`sft-dashboard-proposal-status ${statusClass}`}>{status}</span>
                      </a>
                      <span className="sft-dashboard-proposal-meta">{formatProposalClient(p)}</span>
                      <span className="sft-dashboard-proposal-date">{formatProposalDate(p.date)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      );
    }
    if (id === 'recent_clients') {
      const formatClientName = (c) => {
        const name = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
        const company = c.company || '';
        if (company && name) return `${name} • ${company}`;
        if (company) return company;
        if (name) return name;
        return c.email || '—';
      };
      return (
        <div key="recent_clients" className="sft-dashboard-panel">
          <div className="sft-dashboard-panel-head">
            <h3 className="sft-dashboard-panel-title">Recent Clients</h3>
            <a href={VIEW_ALL_LINKS.recent_clients} className="sft-dashboard-view-all-btn">View All</a>
          </div>
          <div className="sft-dashboard-panel-body">
            {recentClients.length === 0 ? (
              <p className="sft-hint">No clients</p>
            ) : (
              <ul className="sft-dashboard-proposals-list">
                {recentClients.map((c) => (
                  <li key={c._id} className="sft-dashboard-proposal-item">
                    <a href={`/clients/${c._id}`} className="sft-dashboard-proposal-link">
                      {formatClientName(c)}
                    </a>
                    <span className="sft-dashboard-proposal-date">{formatProposalDate(c.createdAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      );
    }
    if (id === 'sales_reminders' && (user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'sales')) {
      return (
        <div key="sales_reminders" className="sft-dashboard-panel">
          <div className="sft-dashboard-panel-head">
            <h3 className="sft-dashboard-panel-title">Sales reminders (today)</h3>
            <a href={VIEW_ALL_LINKS.sales_reminders} className="sft-dashboard-view-all-btn">View All</a>
          </div>
          <div className="sft-dashboard-panel-body">
            {salesReminders.length === 0 ? (
              <p className="sft-hint">No reminders for today</p>
            ) : (
              <ul className="sft-dashboard-tasks-list">
                {salesReminders.map((r) => {
                  const href = r.leadId?._id ? `/sales/leads/${r.leadId._id}` : r.developerId?._id ? `/sales/developers/${r.developerId._id}` : '/sales/reminders';
                  const label = r.leadId ? [r.leadId.firstName, r.leadId.lastName].filter(Boolean).join(' ') || r.leadId.company || r.leadId.email : r.developerId ? r.developerId.companyName || r.developerId.city : r.note || '—';
                  return (
                  <li key={r._id} className="sft-dashboard-task-item">
                    <a href={href} className="sft-dashboard-task-link">
                      {r.type} — {label}
                    </a>
                    <span className="sft-dashboard-task-date">
                      {formatDisplayTime(r.dueAt)}
                    </span>
                  </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      );
    }
    if (id === 'patrol_data_changed') {
      return (
        <div key="patrol_data_changed" className="sft-dashboard-panel">
          <div className="sft-dashboard-panel-head">
            <h3 className="sft-dashboard-panel-title">Patrol – data updated, resend required</h3>
            <a href={VIEW_ALL_LINKS.patrol_data_changed} className="sft-dashboard-view-all-btn">View All</a>
          </div>
          <div className="sft-dashboard-panel-body">
            <p className="sft-hint" style={{ marginBottom: 8 }}>
              Data in client anketa updated. Resend required.
            </p>
            {patrolDataChanged.length === 0 ? (
              <p className="sft-hint">None</p>
            ) : (
              <ul className="sft-dashboard-proposals-list">
                {patrolDataChanged.slice(0, 8).map((a) => (
                  <li key={a._id} className="sft-dashboard-proposal-item">
                    <a href="/patrol-admin?dataChanged=yes" className="sft-dashboard-proposal-link">
                      {a.main?.clientName || '—'}
                    </a>
                  </li>
                ))}
                {patrolDataChanged.length > 8 && (
                  <li className="sft-hint" style={{ marginTop: 4 }}>+{patrolDataChanged.length - 8} more</li>
                )}
              </ul>
            )}
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="sft-dashboard-page">
      <div className="sft-dashboard-header">
        <h2 style={{ margin: 0 }}>Dashboard</h2>
        <div className="sft-dashboard-header-right">
          <span className="sft-dashboard-date">{today}</span>
          <button type="button" className="sft-btn" onClick={openModal}>
            Customize dashboard
          </button>
        </div>
      </div>

      {/* Белая подложка с закруглёнными краями под карточками */}
      <div className="sft-dashboard-cards-wrap">
        <div className="sft-dashboard-grid-3x3">
          {idsToShow.map((id) => renderCard(id))}
        </div>
      </div>

      {showModal && createPortal(
        <div className="sft-modal-overlay" onClick={closeModal} role="dialog" aria-modal="true">
          <div className="sft-modal sft-dashboard-cards-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="sft-dashboard-cards-modal-title">Dashboard cards</h3>
            <p className="sft-dashboard-cards-modal-subtitle">
              Choose which cards to show on your dashboard.
            </p>
            <ul className="sft-dashboard-cards-list">
              {((!user || user?.companyType === 'other') ? GRID_ORDER.filter((id) => id !== 'patrol_data_changed') : GRID_ORDER).map((id) => (
                <li key={id} className="sft-dashboard-cards-list-item">
                  <label className="sft-checkbox-label">
                    <input
                      type="checkbox"
                      className="sft-checkbox-input"
                      checked={draftIds.includes(id)}
                      onChange={() => toggleDraft(id)}
                    />
                    <span className="sft-checkbox-box" />
                    <span>{CARD_LABELS[id] || id}</span>
                  </label>
                </li>
              ))}
            </ul>
            <div className="sft-dashboard-cards-modal-actions">
              <button type="button" className="sft-btn ghost" onClick={closeModal}>
                Cancel
              </button>
              <button type="button" className="sft-btn" onClick={saveCards}>
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function MetricCard({ id, user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const API_URL = '';

  useEffect(() => {
    if (!user) return;
    const ctrl = new AbortController();
    setLoading(true);
    if (id === 'products') {
      axios.get(API_URL + '/products', { withCredentials: true, signal: ctrl.signal })
        .then((r) => setData({ value: Array.isArray(r.data) ? r.data.length : 0 }))
        .catch(() => setData({ value: '—' }))
        .finally(() => setLoading(false));
    } else if (id === 'clients') {
      axios.get(API_URL + '/clients', { withCredentials: true, signal: ctrl.signal })
        .then((r) => setData({ value: Array.isArray(r.data) ? r.data.length : 0 }))
        .catch(() => setData({ value: 0 }))
        .finally(() => setLoading(false));
    } else if (id === 'proposals') {
      axios.get(API_URL + '/proposals', { withCredentials: true, signal: ctrl.signal })
        .then((r) => setData({ value: Array.isArray(r.data) ? r.data.length : 0 }))
        .catch(() => setData({ value: '—' }))
        .finally(() => setLoading(false));
    } else if (id === 'warehouses') {
      axios.get(API_URL + '/warehouse/warehouses', { withCredentials: true, signal: ctrl.signal })
        .then((r) => setData({ value: Array.isArray(r.data) ? r.data.length : 0 }))
        .catch(() => setData({ value: '—' }))
        .finally(() => setLoading(false));
    } else if (id === 'subscriptions') {
      axios.get(API_URL + '/dashboard/subscription-stats', { withCredentials: true, signal: ctrl.signal })
        .then((r) => {
          const total = Number(r.data?.total) || 0;
          const expiring = Number(r.data?.expiring) || 0;
          const expired = Number(r.data?.expired) || 0;
          const byProduct = Array.isArray(r.data?.byProduct) ? r.data.byProduct : [];
          setData({ total, expiring, expired, byProduct });
        })
        .catch(() => setData({ total: 0, expiring: 0, expired: 0, byProduct: [] }))
        .finally(() => setLoading(false));
    } else if (id === 'stock_value' || id === 'receipts_7d' || id === 'issues_7d') {
      axios.get(API_URL + '/dashboard/stats', { withCredentials: true, signal: ctrl.signal })
        .then((r) => {
          const stats = r.data || {};
          if (id === 'stock_value') {
            const v = Number(stats.stockValue);
            const formatted = Number.isNaN(v) ? '—' : '€' + (v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','));
            setData({ value: formatted });
          } else if (id === 'receipts_7d') {
            setData({ value: Number(stats.receipts7d) || 0 });
          } else {
            setData({ value: Number(stats.issues7d) || 0 });
          }
        })
        .catch(() => {
          setData({ value: id === 'stock_value' ? '—' : 0 });
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
      setData({ value: '—' });
    }
    return () => ctrl.abort();
  }, [id, user]);

  const href = VIEW_ALL_LINKS[id] || '#';
  const label = CARD_LABELS[id] || id;

  if (id === 'subscriptions') {
    const total = loading ? '…' : (data?.total ?? 0);
    const expiring = loading ? '…' : (data?.expiring ?? 0);
    const expired = loading ? '…' : (data?.expired ?? 0);
    const active = typeof total === 'number' && typeof expiring === 'number' && typeof expired === 'number'
      ? Math.max(0, total - expiring - expired)
      : (loading ? '…' : '—');
    const byProduct = data?.byProduct || [];
    return (
      <div className="sft-dashboard-metric-card">
        <div className="sft-dashboard-metric-label">Subscriptions</div>
        <div className="sft-dashboard-metric-subs">
          <div className="sft-dashboard-sub-active">{active} Active</div>
          <div className="sft-dashboard-sub-expiring">{expiring} Expiring this month</div>
          <div className="sft-dashboard-sub-expired">{expired} Expired</div>
        </div>
        {byProduct.length > 0 && (
          <div className="sft-dashboard-subs-by-product" style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--line)', fontSize: 12 }}>
            <div className="sft-hint" style={{ marginBottom: 4 }}>By product</div>
            {byProduct.map((row, idx) => (
              <div key={String(row.productId || '') || row.productName || idx} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.productName || ''}>{row.productName || '—'}</span>
                <span style={{ fontWeight: 600, flexShrink: 0 }}>{row.total}</span>
              </div>
            ))}
          </div>
        )}
        <a href={href} className="sft-dashboard-view-all-btn">View All</a>
      </div>
    );
  }

  const displayValue = loading ? '…' : (data?.value ?? '—');

  return (
    <div className="sft-dashboard-metric-card">
      <div className="sft-dashboard-metric-value">{displayValue}</div>
      <div className="sft-dashboard-metric-label">{label}</div>
      <a href={href} className="sft-dashboard-view-all-btn">View All</a>
    </div>
  );
}
